
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, AlertCircle, CalendarDays } from 'lucide-react';
import { fetchAttendanceCard } from '../../store/slices/attendanceSlice';
import AttendanceCardView from './AttendanceCardView';
import MonthNavigator from './MonthNavigator';
import EmployeeAvatar from './EmployeeAvatar';

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function AttendanceModal({ serviceNo, employeeName, isOpen, onClose }) {
  const dispatch = useDispatch();
  const [month, setMonth] = useState(currentMonth());
  const [selectedDate, setSelectedDate] = useState('');
  const { rows, loading, error, serviceNo: loadedFor, month: loadedMonth } =
    useSelector((s) => s.attendance);

  useEffect(() => {
    if (!isOpen || !serviceNo) return;
    dispatch(fetchAttendanceCard({ serviceNo, month }));
  }, [isOpen, serviceNo, month]);

  const handlePickDate = (value) => {
    setSelectedDate(value);
    if (!value) return;
    const m = value.slice(0, 7); 
    if (m !== month) setMonth(m);
  };

  if (!isOpen) return null;

  const isCurrent = String(loadedFor || '').trim() === String(serviceNo || '').trim() && String(loadedMonth || '').trim() === String(month || '').trim();

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-5 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <EmployeeAvatar
                serviceNo={serviceNo}
                initials={(employeeName || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('') || '?'}
                size={44}
              />
              <div className="min-w-0">
                <p className="text-white font-bold text-base truncate">{employeeName || 'Employee'} — Attendance</p>
                <p className="text-white/60 text-xs font-mono mt-0.5">Service No: {serviceNo}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white transition-colors shrink-0">
              <X size={20} />
            </button>
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <MonthNavigator month={month} onChange={(m) => { setMonth(m); setSelectedDate(''); }} dark />
            <label className="flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-xl px-2.5 py-1.5 text-white/80 text-xs cursor-pointer hover:bg-white/20 transition-colors">
              <CalendarDays size={13} />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => handlePickDate(e.target.value)}
                className="bg-transparent text-xs text-white outline-none [color-scheme:dark] cursor-pointer"
              />
            </label>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 bg-gray-50">
          {loading || !isCurrent ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-9 h-9 border-4 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Loading attendance...</p>
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <AlertCircle size={14} className="shrink-0" /> {error}
            </div>
          ) : (
            <AttendanceCardView
              rows={rows}
              month={month}
              onSelectMonth={(m) => { setMonth(m); setSelectedDate(''); }}
              highlightDay={selectedDate && selectedDate.slice(0, 7) === month ? Number(selectedDate.slice(8, 10)) : null}
            />
          )}
        </div>
      </div>
    </div>
  );
}
